import React from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card } from "@/components/Primitives";
import { BRANDING } from "@/config/branding";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LifeBuoy, Mail, Bug, Lightbulb, ShieldCheck, FileText, HelpCircle, Sparkles } from "lucide-react";

const SUPPORT_EMAIL = "enquiries@finetomi.com";

const FAQ = [
  { q: "How does the Financial Inbox work?", a: "Upload any receipt, bill, invoice or statement. Finetomi's AI reads it, suggests a category, title and amount, and shows you everything for review. Once you confirm, it can post straight into your Cash Flow — and every figure stays linked to its original document." },
  { q: "Is Finetomi financial advice?", a: "No. Finetomi is a financial organisation and productivity platform. It provides general information only and is not a licensed financial adviser, accountant or tax agent. Always seek advice from an appropriately qualified professional before making financial decisions." },
  { q: "When will billing start?", a: "During beta your plan is active with no charge. Secure billing via Stripe is coming soon — you'll always be told before anything is charged." },
  { q: "Which countries and currencies are supported?", a: "Finetomi is built for Australia and New Zealand first, with AUD, NZD, USD and EUR available. Report timestamps automatically use your local timezone." },
  { q: "How is my data kept safe?", a: "Your documents and financial data are private to your account and protected behind secure authentication. You remain in control of everything you add." },
];

const WHATS_NEW = {
  sprint: "Sprint 2.2 — Property Workspace",
  items: [
    "Each property now has its own dedicated workspace: Overview, Financials, Mortgage, Property Manager, Insurance, Maintenance, Documents, Calendar and Reports.",
    "Every property transaction links back to its original source document.",
    "Switch between Personal, Portfolio and Community workspaces from Settings — no new account needed (beta).",
    "New Help Centre pages: Privacy Policy, Terms of Use and a live Service Status page.",
  ],
};

function Tile({ icon: Icon, title, desc, href, to, testid }) {
  const inner = (
    <Card className="h-full hover:-translate-y-0.5 transition-transform cursor-pointer">
      <div className="w-11 h-11 rounded-full bg-[#F0E7D8] flex items-center justify-center mb-4">
        <Icon size={20} className="text-[#C6A45C]" strokeWidth={1.5} />
      </div>
      <div className="font-display text-lg text-[#2C2A28]">{title}</div>
      <p className="text-sm text-[#8A7F70] mt-1.5 leading-relaxed">{desc}</p>
    </Card>
  );
  if (to) return <Link data-testid={testid} to={to}>{inner}</Link>;
  return <a data-testid={testid} href={href} target={href.startsWith("mailto") ? undefined : "_blank"} rel="noreferrer">{inner}</a>;
}

export default function Support() {
  return (
    <div data-testid="support-page">
      <PageHeader title="Help Centre" subtitle="We're here to help you get the most from your Financial Home."
        action={<Badge data-testid="app-version" className="bg-[#C6A45C]/15 text-[#8a6d2f] border-[#C6A45C]/30">{BRANDING.version}</Badge>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        <Tile testid="support-contact" icon={Mail} title="Contact support"
          desc={`Email our team at ${SUPPORT_EMAIL} and we'll get back to you.`}
          href={`mailto:${SUPPORT_EMAIL}?subject=Finetomi%20Support`} />
        <Tile testid="support-bug" icon={Bug} title="Report a bug"
          desc="Something not working as expected? Let us know the details."
          href={`mailto:${SUPPORT_EMAIL}?subject=Bug%20Report%20-%20Finetomi`} />
        <Tile testid="support-feature" icon={Lightbulb} title="Feature request"
          desc="Have an idea that would make Finetomi better? We'd love to hear it."
          href={`mailto:${SUPPORT_EMAIL}?subject=Feature%20Request%20-%20Finetomi`} />
        <Tile testid="support-privacy" icon={ShieldCheck} title="Privacy Policy"
          desc="How we collect, use and protect your information." to="/privacy" />
        <Tile testid="support-terms" icon={FileText} title="Terms of Use"
          desc="The terms that govern your use of Finetomi." to="/terms" />
        <Tile testid="support-status" icon={LifeBuoy} title="Service status"
          desc="Check whether Finetomi services are running smoothly." to="/status" />
      </div>

      <Card className="mb-8" data-testid="whats-new">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={20} className="text-[#C6A45C]" />
          <h2 className="font-display text-xl text-[#2C2A28]">What's New</h2>
          <Badge className="ml-auto bg-[#9FB8C9]/20 text-[#5a7a8c] border-[#9FB8C9]/30">{BRANDING.version}</Badge>
        </div>
        <div className="font-display text-lg text-[#2C2A28] mb-3">{WHATS_NEW.sprint}</div>
        <ul className="space-y-2">
          {WHATS_NEW.items.map((it, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-[#6b6256] leading-relaxed">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C6A45C] shrink-0" /> {it}
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-5">
          <HelpCircle size={20} className="text-[#C6A45C]" />
          <h2 className="font-display text-xl text-[#2C2A28]">Frequently asked questions</h2>
        </div>
        <Accordion type="single" collapsible className="w-full" data-testid="support-faq">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-[#EAE0D2]">
              <AccordionTrigger className="text-[#2C2A28] text-left hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#6b6256] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Card>
    </div>
  );
}
